import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

const ProgressIndicator = ({ currentPage, pages, onSelectCurrentPage, className }) => {
  const handlePageClick = (pageIndex) => {
    // Only allow navigation to completed pages or the next immediate page
    if (pageIndex <= currentPage + 1 && pageIndex >= 0 && pageIndex < pages.length) {
      onSelectCurrentPage(pageIndex);
    }
  };

  const isClickable = (pageIndex) => {
    // Allow clicking on current page, completed pages, or next page
    return pageIndex <= currentPage + 1;
  };

  return (
    <div className={clsx('w-full md:w-[50%] relative mx-auto mb-6', className)}>
      {/* Row 1: Circles and connector lines */}
      <div className="flex items-center mb-3">
        {pages.map((page, index) => (
          <React.Fragment key={index}>
            {/* Step number circle */}
            <button
              onClick={() => handlePageClick(index)}
              disabled={!isClickable(index)}
              className={clsx(
                'w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-all relative z-10',
                {
                  'bg-green-500 text-white': currentPage > index, // Completed steps
                  'bg-green-600 text-white': currentPage === index, // Current step
                  'bg-gray-300 text-gray-500': currentPage < index && isClickable(index), // Future clickable steps
                  'bg-gray-300 text-gray-400 cursor-not-allowed': !isClickable(index), // Non-clickable steps
                  'cursor-pointer hover:bg-green-600': isClickable(index) && currentPage >= index,
                  'cursor-pointer': isClickable(index) && currentPage < index
                }
              )}
            >
              {index + 1}
            </button>

            {/* Connector line between steps */}
            {index < pages.length - 1 && (
              <div className="flex-1 flex items-center">
                <div 
                  className={clsx(
                    'h-0.5 w-full transition-colors duration-300',
                    {
                      'bg-green-500': currentPage > index, // Completed connection
                      'bg-gray-300': currentPage <= index // Incomplete connection
                    }
                  )}
                />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Row 2: Step labels */}
      <div className="flex justify-between">
        {pages.map((page, index) => (
          <button
            key={index}
            onClick={() => handlePageClick(index)}
            disabled={!isClickable(index)}
            className={clsx(
              'text-sm font-medium transition-colors text-center',
              {
                'text-green-500': currentPage === index, // Current step
                'text-gray-900': currentPage > index, // Completed steps
                'text-gray-500': currentPage < index && isClickable(index), // Future steps
                'text-gray-400 cursor-not-allowed': !isClickable(index),
                'cursor-pointer hover:text-green-600': isClickable(index) && currentPage >= index,
                'cursor-pointer hover:text-gray-600': isClickable(index) && currentPage < index
              }
            )}
          >
            {page.title}
          </button>
        ))}
      </div>
    </div>
  );
};

ProgressIndicator.propTypes = {
  className: PropTypes.string,
  /** Current active page index (0-4) */
  currentPage: PropTypes.oneOf([0, 1, 2, 3, 4]).isRequired,
  
  /** Array of page objects with title and component */
  pages: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      component: PropTypes.elementType
    })
  ).isRequired,
  
  /** Callback function called when a page is selected */
  onSelectCurrentPage: PropTypes.func.isRequired
};

export default ProgressIndicator;